import React, { useEffect, useState, useRef } from "react";
import confetti from "canvas-confetti";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { Modal, Box, Button, Typography } from "@mui/material";
import { generateRSAKeyPair } from "./crypto";
import { loadPrivateKeyFromIndexedDB, savePrivateKeyToIndexedDB } from "./indexedDB";
import { useAuthStore } from "../../store/useAuthStore";
import { api } from "../../services/api";
import { useAuth } from "../../hooks/authBackoffice";

const phrases = ["Sua conexão está segura.", "Criptografia ativada com sucesso!"];

const UserRegistrationPairKey = () => {
  const [step, setStep] = useState<"loading" | "success" | "error">("loading");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(5);
  const { setShowKeyModal } = useAuth();

  const authStore = useAuthStore();
  const token = localStorage.getItem("@backoffice:token");

  // Refs para controlar os timeouts e intervalos
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);
  const erasingTimeout = useRef<NodeJS.Timeout | null>(null);
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);

  // Efeito principal para verificar e gerar as chaves na montagem
  useEffect(() => {
    const checkPrivateKey = async () => {
      setStep("loading");
      const userId = authStore.getUser()?.id;
      if (!userId) return; // Guarda de segurança se o usuário não estiver disponível

      const privateKey = await loadPrivateKeyFromIndexedDB(userId);
      if (!privateKey) {
        await generateAndSendKeys();
      } else {
        setStep("success");
        typeWriter();
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        startCountdown();
      }
    };

    if (authStore.getUser()) {
      checkPrivateKey();
    }

    // Função de limpeza para desmontar o componente
    return () => {
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
      if (erasingTimeout.current) clearTimeout(erasingTimeout.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, []); // Dependência vazia garante que execute apenas uma vez na montagem

  // ✨ NOVO useEffect para lidar com o FIM do countdown de forma segura ✨
  useEffect(() => {
    // Se a contagem chegou a zero, e o passo é 'success'...
    if (secondsLeft === 0 && step === "success") {
      // Limpamos o intervalo para garantir que ele pare
      if (countdownInterval.current) clearInterval(countdownInterval.current);
      // Agora, de forma segura, atualizamos o estado do componente pai
      setShowKeyModal(false);
    }
  }, [secondsLeft, step, setShowKeyModal]); // Depende de `secondsLeft` e `step`

  // ✨ FUNÇÃO startCountdown CORRIGIDA ✨
  const startCountdown = () => {
    setSecondsLeft(5); // Reinicia o contador
    // Limpa qualquer intervalo anterior para evitar múltiplos contadores rodando
    if (countdownInterval.current) clearInterval(countdownInterval.current);

    countdownInterval.current = setInterval(() => {
      // A única responsabilidade do intervalo é decrementar o estado.
      // A lógica de fechar o modal foi movida para o useEffect acima.
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  };

  const typeWriter = () => {
    if (charIndex < phrases[phraseIndex].length) {
      setCharIndex((prev) => prev + 1);
      typingTimeout.current = setTimeout(typeWriter, 200);
    } else {
      typingTimeout.current = setTimeout(eraseText, 1500);
    }
  };

  const eraseText = () => {
    if (charIndex > 0) {
      setCharIndex((prev) => prev - 1);
      erasingTimeout.current = setTimeout(eraseText, 30);
    } else {
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
      typingTimeout.current = setTimeout(typeWriter, 500);
    }
  };

  const exportPublicKeyToBase64 = async (key: CryptoKey) => {
    const exportedKey = await crypto.subtle.exportKey("spki", key);
    return btoa(String.fromCharCode(...new Uint8Array(exportedKey)));
  };

  const sendPublicKeyToBackend = async (publicKeyPem: CryptoKey) => {
    const publicKeyPemBase64 = await exportPublicKeyToBase64(publicKeyPem);
    const userName = authStore.getUser()?.userName;
    if (!userName) return; // Guarda de segurança

    await api.patch(
      `/graphic/${userName}/update-publickey`,
      { publicKey: publicKeyPemBase64 },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const generateAndSendKeys = async () => {
    try {
      const userId = authStore.getUser()?.id;
      if (!userId) throw new Error("ID do usuário não encontrado.");

      const keyPair = await generateRSAKeyPair();
      await savePrivateKeyToIndexedDB(userId, keyPair.privateKey);
      await sendPublicKeyToBackend(keyPair.publicKey);

      setStep("success");
      typeWriter();
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      startCountdown();
    } catch (error) {
      console.error("Erro ao gerar e enviar as chaves:", error);
      setStep("error");
    }
  };

  return (
    <Modal
      open={true}
      onClose={() => setShowKeyModal(false)}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1300,
      }}
    >
      <Box
        sx={{
          p: 4,
          borderRadius: 4,
          backgroundColor: "white",
          width: { xs: "90%", sm: 400 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {step === "loading" && (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                mb: 2,
                borderRadius: "50%",
                animation: "spin 1.5s linear infinite",
                background: "conic-gradient(#3b82f6 0%, #f0f0f0 80%)",
                maskImage: "radial-gradient(transparent 45%, black 46%)",
              }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                mb: 1,
                background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Gerando suas chaves de criptografia...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Isso garante que suas mensagens sejam totalmente seguras.
            </Typography>
          </>
        )}

        {step === "success" && (
          <>
            <ShieldCheck size={40} color="#22c55e" />
            <Typography variant="h6" color="success.main" sx={{ mt: 2 }}>
              CHAVES CONFIGURADAS!
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Finalizando em {secondsLeft}s...
            </Typography>
          </>
        )}

        {step === "error" && (
          <>
            <ShieldOff size={40} color="#dc2626" />
            <Typography variant="h6" color="error.main" sx={{ mt: 2 }}>
              Ocorreu um erro ao gerar as chaves.
            </Typography>
            <Typography variant="body2" color="error.main">
              Por favor, tente novamente.
            </Typography>
          </>
        )}

        <Button variant="contained" color="primary" sx={{ mt: 3 }} onClick={() => setShowKeyModal(false)}>
          Fechar
        </Button>
      </Box>
    </Modal>
  );
};

export default UserRegistrationPairKey;
